import {
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { MeasurementToolIcon } from '@/constants/AnnotationIcons';
import { convertInchesToMillimeters, convertMillimetersToInches, localiseMeasurement } from '@/utils/unitSystem';
import {
  unitSystem as unitSystemState,
  allPointOfInterestTemplates,
} from '@/components/Analysis/state';
import { PointOfInterest, UnitsEnum, UnitSystemEnum } from '@/__generated__/graphql';
import { BlisterFieldsData } from '../hooks/useBlisterData';
import { ErrorChip } from './styles';
import {
  UNCERTAIN_PROFILE_SHORT_LABEL,
  ESTIMATED_BLISTER_HEIGHT_LABEL,
  ESTIMATED_PIT_DEPTH_LABEL,
  PIT,
  BLISTER,
  PIT_TYPE,
  BLISTER_TYPE,
} from '@/constants';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import { convertTemplateNameToPoiType } from '@/utils';
import { PointOfInterestTemplateDocument } from '@/types';
import { UncertainFieldHook } from '../hooks/useUncertainField';
import { UncertainReasonField } from './UncertainReasonField';
import { usePoiDefectData } from '../hooks/usePoiDefectData';

type BlisterFormFieldsProps = {
  blisterData: BlisterFieldsData | undefined;
  handleManualHeightChange: (height: string) => void;
  handleMeasureIconClick: () => void;
  onFieldChange?: (value: boolean) => void;
  allDisabled?: boolean;
  uncertainField: UncertainFieldHook;
};

export const BlisterFormFields = ({
  blisterData,
  handleManualHeightChange,
  handleMeasureIconClick,
  onFieldChange,
  uncertainField,
  allDisabled,
}: BlisterFormFieldsProps) => {
  const unitSystem = useAtomValue(unitSystemState);
  const allPoiTemplates = useAtomValue(allPointOfInterestTemplates);
  const [editPointOfInterest, setEditPointOfInterest] = useAtom(poiState.editPointOfInterest);

  const { switchTemplate } = usePoiDefectData();
  const template = editPointOfInterest?.template;
  const poiType = convertTemplateNameToPoiType(template?.name);

  const isPitCheckBoxEnabled = poiType === PIT_TYPE;

  const formatValue = useCallback(
    (value: string | undefined) => {
      if (!value) return '';
      return (
        localiseMeasurement({
          unitSystem,
          value: Number(value),
          sourceUnit: UnitsEnum.Meter,
          displayAsUnit: UnitsEnum.Millimeter,
          roundToDecimal: 3,
        }) ?? ''
      ).replace(/[^\d.]+$/, '');
    },
    [unitSystem]
  );

  const unit = useMemo(() => {
    return unitSystem === UnitSystemEnum.Imperial ? '"' : 'mm';
  }, [unitSystem]);

  const blisterHeight = useMemo(() => {
    if (!blisterData) return undefined;
    return formatValue(blisterData.estimatedHeight);
  }, [blisterData, formatValue]);

  useEffect(() => {
    if (!blisterData?.userManualSetHeight) return;
    const convertHeight = (height: number) => {
      return unitSystem === UnitSystemEnum.Imperial
        ? convertMillimetersToInches(height)
        : convertInchesToMillimeters(height);
    };
    const formatedValue = convertHeight(Number(blisterData.userManualSetHeight));
    handleManualHeightChange(String(formatedValue.toFixed(3)))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitSystem])


  useEffect(() => {
    if (blisterHeight) uncertainField.handleUpdateReason('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blisterHeight]);

  const estimatedHeightColor = useMemo(() => {
    if (blisterData?.manualSetHeight || blisterData?.userManualSetHeight) return '#b0b3b7';
    return 'inherit';
  }, [blisterData?.manualSetHeight, blisterData?.userManualSetHeight]);

  const handleTemplateChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked;

      const newTemplate = allPoiTemplates.find((item) =>
        item.name.includes(newValue ? PIT : BLISTER)
      ) as PointOfInterestTemplateDocument | undefined;

      if (newTemplate) {
        // use switchTemplate to transfer the defect data from the current template to the new one
        const templateData = switchTemplate(newTemplate);
        setEditPointOfInterest((current) => ({
          ...current,
          template: newTemplate,
          pointOfInterest: {
            ...current.pointOfInterest,
            templateData: templateData,
          } as PointOfInterest,
        }));
      }

      if (onFieldChange) onFieldChange(newValue);
    },
    [allPoiTemplates, onFieldChange, setEditPointOfInterest, switchTemplate]
  );

  const pitTemplateExists = useMemo(() => {
    return allPoiTemplates.find((poiTemplate) =>
      poiTemplate.name.toUpperCase().includes(PIT.toUpperCase())
    );
  }, [allPoiTemplates]);

  const estimatedHeightLabel = useMemo(() => {
    switch (poiType) {
      case BLISTER_TYPE:
        return ESTIMATED_BLISTER_HEIGHT_LABEL;
      case PIT_TYPE:
        return ESTIMATED_PIT_DEPTH_LABEL;
      default:
        return '';
    }
  }, [poiType]);

  return (
    <Stack spacing={3} sx={{ width: '100%', marginTop: 3.5 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ color: estimatedHeightColor }}
      >
        <Typography sx={{ fontSize: '1.4rem' }}>{estimatedHeightLabel}</Typography>
        {blisterHeight ? (
          <Typography sx={{ fontWeight: 600, fontSize: '1.6rem' }}>
            {blisterHeight + unit}
          </Typography>
        ) : (
          <ErrorChip>{UNCERTAIN_PROFILE_SHORT_LABEL}</ErrorChip>
        )}
      </Stack>
      <TextField
        label="Manual Set Height"
        name="userManualSetHeight"
        value={blisterData?.userManualSetHeight || blisterData?.manualSetHeight}
        onChange={(event) => handleManualHeightChange(event.target.value)}
        slotProps={{
          inputLabel: {
            shrink: !!blisterData?.manualSetHeight,
          },
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <MeasurementToolIcon sx={{ cursor: 'pointer' }} onClick={handleMeasureIconClick} />
              </InputAdornment>
            ),
          },
        }}
        variant="standard"
        fullWidth
        disabled={allDisabled}
      />
      {blisterHeight && pitTemplateExists && (
        <FormControlLabel
          control={
            <Checkbox
              checked={isPitCheckBoxEnabled}
              onChange={handleTemplateChange}
              disabled={allDisabled}
            />
          }
          label={`Mark this point of interest as Pit`}
        />
      )}
      {uncertainField.fieldValue && (
        <UncertainReasonField uncertainField={uncertainField} disabled={allDisabled} />
      )}
    </Stack>
  );
};
